import { Fragment, useContext, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { MetricDependency, CountMetric } from "overlay-engine";
import {
  ReportWidget,
  TableHeadingsEditor,
  TooltipBooleanConfigurationOption,
} from "./widgets";
import {
  ReportWidgetTooltipControls,
  TooltipMorePopover,
} from "../../editor/TooltipMenu";
import { LabeledDropdown } from "./LabeledDropdown";
import { MetricLoadingDots } from "../components/MetricLoadingDots";
import { useNumberFormatters } from "../hooks/useNumberFormatters";
import {
  PaginationFooter,
  PaginationSetting,
  TablePaddingRows,
} from "./Pagination";
import { usePagination } from "../hooks/usePagination";
import { ClassRowSettingsPopover } from "./ClassRowSettingsPopover";
import ReportLayerVisibilityCheckbox from "../components/ReportLayerVisibilityCheckbox";
import { LayersIcon } from "@radix-ui/react-icons";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  DEFAULT_PRESENCE_PRESENTATION,
  PresencePresentation,
  getPresencePresentationOptions,
  renderPresenceSymbol,
} from "./PresenceSymbols";
import {
  ClassTableRow,
  ClassTableRowComponentSettings,
  combineMetricsBySource,
  getClassTableRows,
  hasClassTableRowVisibilityToggle,
  resolveClassTableRowStableId,
  shouldTruncateClassTableRowLabels,
} from "./ClassTableRows";
import {
  classTableRowHasSwatch,
  SwatchForClassTableRow,
} from "./SwatchForClassTableRow";
import { useOverlaySources } from "../hooks/useOverlaySources";
import { usePrimaryGeography } from "../hooks/usePrimaryGeography";
import CollectionExpandableName from "./collection/CollectionExpandableName";
import { sketchContributionsForClassTableRow } from "./collection/sketchContributions";
import { useCollectionSketchExpand } from "./collection/useCollectionSketchExpand";
import { ReportUIStateContext } from "../context/ReportUIStateContext";

export type FeaturePresenceTableSettings = {
  rowsPerPage?: number;
  nameLabel?: string;
  presenceLabel?: string;
  presenceColumnPresentation?: PresencePresentation;
  bufferMeters?: number;
  hideColorSwatches?: boolean;
} & ClassTableRowComponentSettings;

type FeaturePresenceRow = ClassTableRow & {
  count: number;
  geographyTotal?: number;
};

export const FeaturePresenceTable: ReportWidget<
  FeaturePresenceTableSettings
> = ({
  metrics,
  componentSettings,
  sources,
  loading,
  sketchClass,
  geographies,
  dependencies,
}) => {
  const { t } = useTranslation("reports");
  const rowsPerPage = componentSettings.rowsPerPage ?? 10;
  const showColorSwatches = !componentSettings.hideColorSwatches;
  const nameLabel = componentSettings.nameLabel || t("Name");
  const presenceLabel = componentSettings.presenceLabel || t("Presence");
  const truncateRowLabels = shouldTruncateClassTableRowLabels(componentSettings);

  const { clippingGeography } = usePrimaryGeography(sketchClass, geographies);
  const primaryGeographyId = clippingGeography?.id;
  const { printing } = useContext(ReportUIStateContext);

  const rows = useMemo<FeaturePresenceRow[]>(() => {
    const classRows = getClassTableRows({
      dependencies,
      sources,
      customLabels: componentSettings.customRowLabels,
      allFeaturesLabel: t("All features"),
      stableIds: componentSettings.rowLinkedStableIds,
      excludedRowKeys: componentSettings.excludedRowKeys,
      includeAllFeaturesRowForGroupedSources:
        componentSettings.includeAllFeaturesRowForGroupedSources,
    });

    if (sources.length === 0 || metrics.length === 0 || loading) {
      return classRows.map((r) => ({
        ...r,
        count: NaN,
        geographyTotal: NaN,
      }));
    }

    if (!primaryGeographyId) {
      throw new Error("Primary geography not found.");
    }

    const combinedMetrics = combineMetricsBySource<CountMetric>(
      metrics,
      sources,
      primaryGeographyId
    );

    let rows: FeaturePresenceRow[] = classRows.map((r) => {
      const combinedForSource = combinedMetrics[r.sourceId];
      const count =
        combinedForSource?.fragments?.value?.[r.groupByKey]?.count || 0;
      const geographyTotal =
        combinedForSource?.geographies?.value?.[r.groupByKey]?.count || 0;
      return {
        ...r,
        count,
        geographyTotal,
      };
    });

    rows = rows.sort((a, b) =>
      (a.label || a.key).localeCompare(b.label || b.key)
    );

    return rows;
  }, [
    dependencies,
    sources,
    t,
    metrics,
    loading,
    primaryGeographyId,
    componentSettings.customRowLabels,
    componentSettings.rowLinkedStableIds,
    componentSettings.excludedRowKeys,
    componentSettings.includeAllFeaturesRowForGroupedSources,
  ]);

  const {
    currentPage,
    setCurrentPage,
    paginatedItems: paginatedRows,
    paddingRowsCount,
    showPagination,
    totalPages,
    totalRows,
    pageBounds,
  } = usePagination(rows, rowsPerPage);

  const {
    isCollection,
    sketchNameById,
    childSketchIds,
    toggleRow,
    hideCaretExpandTooltip,
    isSketchBreakdownExpanded,
  } = useCollectionSketchExpand(sketchClass, {
    forceAllExpanded: printing,
  });

  const sketchLinesByRowKey = useMemo(() => {
    if (!isCollection || !primaryGeographyId || loading) {
      return new Map<
        string,
        ReturnType<typeof sketchContributionsForClassTableRow>
      >();
    }
    const map = new Map<
      string,
      ReturnType<typeof sketchContributionsForClassTableRow>
    >();
    for (const row of rows) {
      const source = sources.find((s) => s.stableId === row.sourceId);
      if (!source) continue;
      map.set(
        row.key,
        sketchContributionsForClassTableRow({
          metrics,
          source,
          geographyId: primaryGeographyId,
          metricType: "count",
          groupByKey: row.groupByKey,
          childSketchIds,
          geographyDenominator:
            typeof row.geographyTotal === "number" &&
            Number.isFinite(row.geographyTotal)
              ? row.geographyTotal
              : 0,
          sketchNameById,
          t,
        }),
      );
    }
    return map;
  }, [
    isCollection,
    primaryGeographyId,
    loading,
    rows,
    metrics,
    sources,
    childSketchIds,
    sketchNameById,
    t,
  ]);

  const hasAnyColor = useMemo(
    () => showColorSwatches && rows.some(classTableRowHasSwatch),
    [rows, showColorSwatches]
  );
  const hasVisibilityColumn = useMemo(
    () =>
      hasClassTableRowVisibilityToggle(
        rows,
        componentSettings.rowLinkedStableIds
      ),
    [rows, componentSettings.rowLinkedStableIds]
  );

  const hasSwatchColumn =
    showColorSwatches && rows.some(classTableRowHasSwatch);

  if (!loading && !rows.length) {
    return (
      <div className="mt-3 border border-black/10 rounded bg-gray-50 px-3 py-2 text-gray-600 text-sm">
        <Trans ns="reports">No overlapping features found.</Trans>
      </div>
    );
  }

  return (
    <Tooltip.Provider>
      <div className="mt-3 rounded-md border border-gray-200 shadow-sm w-full max-w-full bg-white overflow-hidden">
        <div className="divide-y divide-gray-100">
          <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200">
            {hasVisibilityColumn && (
              <div className="flex-none w-6 flex justify-center text-gray-600 text-xs font-semibold uppercase tracking-wide">
                <LayersIcon className="w-4 h-4" />
              </div>
            )}
            <div className="flex-1 min-w-0 text-gray-600 text-xs font-semibold uppercase tracking-wide">
              {nameLabel}
            </div>
            <div className="flex-none text-gray-600 text-xs font-semibold uppercase tracking-wide min-w-[80px] text-center">
              {presenceLabel}
            </div>
          </div>
          {paginatedRows.map((row) => {
            const stableId = resolveClassTableRowStableId(
              row,
              componentSettings.rowLinkedStableIds
            );
            const isPresent = row.count > 0;
            const displayLabel =
              row.key === "*" ? t("All features") : row.label;
            const expanded =
              isSketchBreakdownExpanded(row.key);
            const sketchLines = sketchLinesByRowKey.get(row.key) ?? [];
            return (
              <Fragment key={row.key}>
              <div
                className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50`}
              >
                {hasVisibilityColumn && (
                  <div className="flex-none w-6 flex justify-center">
                    {stableId ? (
                      <ReportLayerVisibilityCheckbox stableId={stableId} />
                    ) : (
                      <span className="text-xs text-gray-400"></span>
                    )}
                  </div>
                )}
                {showColorSwatches && <SwatchForClassTableRow row={row} />}
                <div className="flex-1 min-w-0 text-gray-800 text-sm">
                  <CollectionExpandableName
                    displayLabel={displayLabel}
                    truncateRowLabels={truncateRowLabels}
                    expanded={expanded}
                    onToggle={() => toggleRow(row.key)}
                    loading={loading}
                    isCollection={isCollection}
                    caretTooltipEnabled={!hideCaretExpandTooltip}
                    caretTooltipLabel={t("Expand sketch details")}
                    expandAriaLabelExpanded={t(
                      "Collapse sketch breakdown for {{name}}",
                      { name: displayLabel },
                    )}
                    expandAriaLabelCollapsed={t(
                      "Expand sketch breakdown for {{name}}",
                      { name: displayLabel },
                    )}
                  />
                </div>
                <div className="flex-none text-gray-900 tabular-nums text-sm min-w-[80px] text-center items-center flex">
                  {loading ? (
                    <MetricLoadingDots />
                  ) : (
                    renderPresenceSymbol({
                      isPresent,
                      presentation:
                        componentSettings.presenceColumnPresentation ||
                        DEFAULT_PRESENCE_PRESENTATION,
                      presentLabel: t("Present"),
                      absentLabel: t("Absent"),
                      withTooltip: true,
                      wrapperClassName:
                        "inline-flex items-center justify-center w-full cursor-help",
                    })
                  )}
                </div>
              </div>
              {isCollection && expanded && sketchLines.length === 0 && (
                <div className="flex flex-wrap items-center gap-3 border-t border-slate-200/80 bg-slate-100 px-3 py-2.5 text-sm italic text-gray-600">
                  <div className="flex-none w-6" aria-hidden />
                  {hasSwatchColumn && (
                    <div className="flex-none w-4" aria-hidden />
                  )}
                  <div className="min-w-0 flex-1">
                    {t(
                      "No individual sketches contributed to this category.",
                    )}
                  </div>
                </div>
              )}
              {isCollection &&
                expanded &&
                sketchLines.map((sk) => (
                  <div
                    key={`${row.key}-sketch-${sk.sketchId}`}
                    className="flex flex-wrap items-center gap-3 border-t border-slate-200/80 bg-slate-100 px-3 py-2 hover:bg-slate-200/30"
                  >
                    {hasVisibilityColumn && (
                      <div className="flex-none w-6" aria-hidden />
                    )}
                    {hasSwatchColumn && (
                      <div className="flex-none w-4 flex justify-center" aria-hidden />
                    )}
                    <div className="min-w-0 flex-1 text-sm text-gray-800">
                      <span className="min-w-0">{sk.sketchName}</span>
                    </div>
                    <div className="flex-none text-gray-900 tabular-nums text-sm min-w-[80px] text-center items-center flex">
                      {loading ? (
                        <MetricLoadingDots />
                      ) : (
                        renderPresenceSymbol({
                          isPresent: sk.primaryValue > 0,
                          presentation:
                            componentSettings.presenceColumnPresentation ||
                            DEFAULT_PRESENCE_PRESENTATION,
                          presentLabel: t("Present"),
                          absentLabel: t("Absent"),
                          withTooltip: true,
                          wrapperClassName:
                            "inline-flex items-center justify-center w-full cursor-help",
                        })
                      )}
                    </div>
                  </div>
                ))}
              </Fragment>
            );
          })}
          <TablePaddingRows
            count={paddingRowsCount}
            includeColorColumn={hasAnyColor}
            includeVisibilityColumn={hasVisibilityColumn}
            showPercentColumn={false}
            numericAlign="center"
          />
        </div>
        {showPagination && (
          <PaginationFooter
            currentPage={currentPage}
            totalPages={totalPages}
            totalRows={totalRows}
            pageBounds={pageBounds}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </Tooltip.Provider>
  );
};

export const FeaturePresenceTableTooltipControls: ReportWidgetTooltipControls =
  ({
    node,
    onUpdate,
    onUpdateDependencyParameters,
    onUpdateAllDependencies,
  }) => {
    const { t } = useTranslation("admin:reports");
    const dependencies = node.attrs?.metrics as MetricDependency[] | undefined;

    const settings: FeaturePresenceTableSettings = useMemo(
      () => node.attrs?.componentSettings || {},
      [node.attrs?.componentSettings]
    );

    const rowsPerPage = settings.rowsPerPage ?? 10;

    const { filteredSources: sources } = useOverlaySources(dependencies);

    const handleUpdate = (patch: Partial<FeaturePresenceTableSettings>) => {
      onUpdate({
        componentSettings: {
          ...settings,
          ...patch,
        },
      });
    };

    const buffer = ((node.attrs?.metrics || []) as MetricDependency[]).find(
      (m) => m.parameters?.bufferDistanceKm !== undefined
    )?.parameters?.bufferDistanceKm;

    const handleBufferClick = () => {
      const currentValue = buffer !== undefined ? String(buffer) : "0";
      const value = window.prompt(
        t("Enter buffer distance in kilometers (or 0 for none)"),
        currentValue
      );
      if (value === null) {
        return;
      }
      const numValue = value === "" || value === "0" ? 0 : Number(value);
      onUpdateDependencyParameters((dependency) => {
        if (dependency.subjectType === "geographies") {
          return {
            ...dependency.parameters,
            bufferDistanceKm: undefined,
          };
        } else {
          return {
            ...dependency.parameters,
            bufferDistanceKm: numValue === 0 ? undefined : numValue,
          };
        }
      });
    };

    const bufferFormatter = useNumberFormatters({
      unit: "kilometer",
      unitDisplay: "short",
    });

    const presencePresentationOptions = getPresencePresentationOptions();

    return (
      <div className="flex gap-3 items-center text-sm text-gray-800">
        <LabeledDropdown
          label={t("Symbol")}
          value={
            settings.presenceColumnPresentation || DEFAULT_PRESENCE_PRESENTATION
          }
          options={presencePresentationOptions}
          onChange={(value) =>
            handleUpdate({
              presenceColumnPresentation: value as PresencePresentation,
            })
          }
        />
        <TableHeadingsEditor
          labelKeys={["nameLabel", "presenceLabel"]}
          labelDisplayNames={[t("Name"), t("Presence")]}
          componentSettings={settings}
          onUpdate={onUpdate}
        />
        <ClassRowSettingsPopover
          settings={settings}
          onUpdateSettings={(patch) => handleUpdate(patch)}
          dependencies={dependencies || []}
          sources={sources}
          onUpdateDependencyParameters={onUpdateDependencyParameters}
          onUpdateAllDependencies={onUpdateAllDependencies}
          t={t}
          showColorSwatches={!settings.hideColorSwatches}
          onShowColorSwatchesChange={(next) =>
            handleUpdate({ hideColorSwatches: next ? undefined : true })
          }
        />
        <TooltipMorePopover>
          <button
            type="button"
            onClick={handleBufferClick}
            className="w-full text-left text-sm rounded hover:text-black focus:outline-none flex items-center space-x-2"
          >
            <span className="font-light text-gray-400">{t("Buffer")}</span>
            <span className="flex-1 text-right hover:ring hover:ring-blue-300/20">
              {bufferFormatter.distance(buffer ?? 0)}
            </span>
          </button>
          <PaginationSetting
            rowsPerPage={rowsPerPage}
            onChange={(next: number) => handleUpdate({ rowsPerPage: next })}
          />
          <TooltipBooleanConfigurationOption
            label={t("Truncate row labels")}
            checked={shouldTruncateClassTableRowLabels(settings)}
            checkboxFirst
            onChange={(next) =>
              handleUpdate({
                disableRowLabelTruncation: next ? undefined : true,
              })
            }
          />
          <div className="flex">
            <span className="text-sm font-light text-gray-400 whitespace-nowrap pr-1">
              {t("Component Type")}
            </span>
            <span className="text-sm font-light whitespace-nowrap px-1 flex-1 text-right">
              {t("Feature Presence Table")}
            </span>
          </div>
        </TooltipMorePopover>
      </div>
    );
  };
